import { DataSource } from '@angular/cdk/table'
import { ErrorHandler, Injectable } from '@angular/core'
import { PageEvent } from '@angular/material/paginator'
import { Sort } from '@angular/material/sort'

import { BehaviorSubject, Observable, combineLatest, of as observableOf } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'

import { LaskuToimittaja } from '@lemon/core-firebase/model/lasku'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'

interface Hakutiedot {
  vapaahaku: string
  naytaPoistetut: boolean
  sivutustiedot: PageEvent
  lajittelutiedot: Sort
}

/**
 * Read-only datasource lemonator-puolen toimittajien selaukselle. Kysyy customers/{asiakasAvain}/lasku-toimittajat
 * Lemonaid-firestoresta ja suodattaa client-puolella nimen, y-tunnuksen, numeron tai IBANin mukaan.
 */
@Injectable()
export class ToimittajatSelaaDataSourceService extends DataSource<LaskuToimittaja> {

  private _hakutiedot: Hakutiedot = {
    vapaahaku: '',
    naytaPoistetut: false,
    sivutustiedot: { length: 0, pageIndex: 0, pageSize: 20 },
    lajittelutiedot: { active: 'numero', direction: 'asc' },
  }
  private _hakutiedotSubject = new BehaviorSubject<Hakutiedot>(this._hakutiedot)
  hakutiedotObservable: Observable<Hakutiedot> = this._hakutiedotSubject.asObservable()

  private _lataaSubject = new BehaviorSubject<boolean>(true)
  lataaObservable: Observable<boolean> = this._lataaSubject.asObservable()

  private _rivienMaaraSubject = new BehaviorSubject<number>(0)
  rivienMaaraObservable: Observable<number> = this._rivienMaaraSubject.asObservable()

  private _kaikkiObservable: Observable<LaskuToimittaja[]>
  nakyvatObservable: Observable<LaskuToimittaja[]>

  constructor(
    private _errorHandler: ErrorHandler,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _asiakasService: AsiakasService,
    private _laskuUriService: LaskuUriService,
  ) {
    super()

    this._kaikkiObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      tap(() => this._lataaSubject.next(true)),
      switchMap(avainTiedot => {
        if (!avainTiedot?.avain) {
          return observableOf<LaskuToimittaja[]>([])
        }
        const uri = this._laskuUriService.getLaskuToimittajatCollectionUri(avainTiedot.avain)
        return this._firebaseLemonaid.firestoreCollection<LaskuToimittaja>(uri).listenSnapshots().pipe(
          map(snapshots => snapshots.map(snap => {
            const t = snap.data()
            t.avain = snap.id
            return t
          })),
        )
      }),
      tap(() => this._lataaSubject.next(false)),
      lemonShare(),
    )

    this.nakyvatObservable = combineLatest([this._kaikkiObservable, this._hakutiedotSubject]).pipe(
      map(([toimittajat, hakutiedot]) => {
        let suodatetut = hakutiedot.naytaPoistetut ? toimittajat : toimittajat.filter(t => !t.poistettu)

        const hakuteksti = hakutiedot.vapaahaku?.trim().toLowerCase()
        if (hakuteksti) {
          suodatetut = suodatetut.filter(t => {
            const nimi = t.nimi?.toLowerCase() ?? ''
            const maksunSaaja = t.maksunSaaja?.toLowerCase() ?? ''
            const ytunnus = t.ytunnus?.toLowerCase() ?? ''
            const numero = String(t.numero ?? '')
            const ibanit = Object.keys(t.iban ?? {}).join(' ').toLowerCase()
            return nimi.includes(hakuteksti)
              || maksunSaaja.includes(hakuteksti)
              || ytunnus.includes(hakuteksti)
              || numero.includes(hakuteksti)
              || ibanit.includes(hakuteksti)
          })
        }

        const { active, direction } = hakutiedot.lajittelutiedot
        if (active && direction) {
          const suunta = direction === 'asc' ? 1 : -1
          suodatetut = suodatetut.slice().sort((a, b) => this._vertaa(a, b, active) * suunta)
        }

        this._rivienMaaraSubject.next(suodatetut.length)

        const { pageIndex, pageSize } = hakutiedot.sivutustiedot
        const alku = pageIndex * pageSize
        return suodatetut.slice(alku, alku + pageSize)
      }),
      lemonShare(),
    )
  }

  private _vertaa(a: LaskuToimittaja, b: LaskuToimittaja, kentta: string): number {
    if (kentta === 'numero') {
      return (a.numero ?? 0) - (b.numero ?? 0)
    }
    if (kentta === 'iban') {
      const ibanA = this._oletusTaiEnsimmainenIban(a)
      const ibanB = this._oletusTaiEnsimmainenIban(b)
      return ibanA.localeCompare(ibanB)
    }
    const arvoA = String((a as unknown as Record<string, unknown>)[kentta] ?? '').toLowerCase()
    const arvoB = String((b as unknown as Record<string, unknown>)[kentta] ?? '').toLowerCase()
    return arvoA.localeCompare(arvoB)
  }

  private _oletusTaiEnsimmainenIban(t: LaskuToimittaja): string {
    const ibanit = Object.values(t.iban ?? {})
    if (!ibanit.length) {
      return ''
    }
    const oletus = ibanit.find(i => i.tyyppi === 'oletus')
    return (oletus ?? ibanit[0]).iban ?? ''
  }

  setSearch(vapaahaku: string) {
    this._hakutiedot = { ...this._hakutiedot, vapaahaku, sivutustiedot: { ...this._hakutiedot.sivutustiedot, pageIndex: 0 } }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  setNaytaPoistetut(naytaPoistetut: boolean) {
    this._hakutiedot = { ...this._hakutiedot, naytaPoistetut, sivutustiedot: { ...this._hakutiedot.sivutustiedot, pageIndex: 0 } }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  setSort(sort: Sort) {
    this._hakutiedot = { ...this._hakutiedot, lajittelutiedot: sort, sivutustiedot: { ...this._hakutiedot.sivutustiedot, pageIndex: 0 } }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  setPage(page: PageEvent) {
    this._hakutiedot = { ...this._hakutiedot, sivutustiedot: page }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  get sort(): Sort {
    return this._hakutiedot.lajittelutiedot
  }

  connect(): Observable<LaskuToimittaja[]> {
    return this.nakyvatObservable
  }

  disconnect() {}
}
